import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    CreateCurrencyConfigurationCommand,
    CurrencyConfigurationDto,
    CurrencyResultDto,
    CurrencyService,
    FEATURE_CURRENCY,
    FlyFreelyError,
    FlyFreelyLoggingService,
    JurisdictionService,
    OperatingCategoryValuesDtoObject,
    OperatingCategoryValuesDtoOperatingCategoryDto,
    PersonRolesDto,
    PersonService,
    PersonWithRoles,
    UpdateCurrencyConfigurationCommand,
    WorkTracker,
    operatingConditionsTypeLookup,
    operatingConditionsValueLookup
} from '@flyfreely-portal-ui/flyfreely';
import {
    NgSelectPaginationValues,
    NgSelectSearch,
    ngSelectPreSearchConfigure,
    updateNgSelectData
} from '@flyfreely-portal-ui/ng-select';
import { FormatMTOWPipe, personSearch } from '@flyfreely-portal-ui/ui';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import {
    BehaviorSubject,
    Observable,
    Subject,
    debounceTime,
    firstValueFrom,
    forkJoin,
    map,
    of
} from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

interface CurrencyConfigFormGroup {
    currencyOperatingConditionIdList: FormControl<number[]>;
    requiredOperatingConditionIdList: FormControl<number[]>;
    days: FormControl<number>;
    requiredFlights: FormControl<number>;
    requiredFlightTimeSeconds: FormControl<number>;
    usingThisAuthority: FormControl<boolean>;
}

@Component({
    selector: 'currency-configuration-dialogue',
    templateUrl: './currency-configuration.component.html'
})
export class CurrencyConfigurationDialogue implements OnInit, OnDestroy {
    @Input() airspaceJurisdictionId: number | null;
    @Input() personnelRegisterId: number;
    @Input() managingOrganisationId: number;

    operatingConditions: {
        type: string;
        value: string;
        id: number;
    }[];
    private currencyConfigurationsSubject = new BehaviorSubject<
        CurrencyConfigurationDto[]
    >([]);
    currencyConfigurations$ = this.currencyConfigurationsSubject.asObservable();
    currencyPreviewMap: Record<number, CurrencyResultDto> = {};
    editingConfig: number;
    currencyFeatureFlag = [FEATURE_CURRENCY];
    pilots: PersonWithRoles[] = [];
    selectedPilotId: number;
    personSearch = personSearch.bind(this);
    loadingPilots = false;

    private pilotSearchSource = new Subject<NgSelectSearch>();
    pilotSelectConfig: NgSelectPaginationValues = {
        currentPage: 0,
        preSearchPage: null,
        searchValue: null,
        totalItems: null
    };

    readonly operatingConditionsTypeLookup = operatingConditionsTypeLookup;
    readonly operatingConditionsValueLookup = operatingConditionsValueLookup;

    private workTracker = new WorkTracker();
    working = false;

    private ngUnsubscribe$ = new Subject<void>();

    // the ID of the entry being edited
    editing: number;
    formGroup: FormGroup<{
        add: FormGroup<CurrencyConfigFormGroup>;
        edit: FormGroup<CurrencyConfigFormGroup>;
    }>;

    constructor(
        public modal: BsModalRef<CurrencyConfigurationDialogue>,
        modalOptions: ModalOptions,
        private logging: FlyFreelyLoggingService,
        private currencyService: CurrencyService,
        private jurisdictionService: JurisdictionService,
        private commonDialoguesService: CommonDialoguesService,
        private formatMTOWPipe: FormatMTOWPipe,
        private personService: PersonService
    ) {
        modalOptions.closeInterceptor = () => {
            if (this.editingConfig != null) {
                this.editingConfig = null;
                return Promise.reject();
            }
            return Promise.resolve();
        };

        this.formGroup = new FormGroup({
            add: new FormGroup({
                currencyOperatingConditionIdList: new FormControl(
                    [],
                    [Validators.required]
                ),
                requiredOperatingConditionIdList: new FormControl([]),
                days: new FormControl(undefined),
                requiredFlights: new FormControl(0, [Validators.required]),
                requiredFlightTimeSeconds: new FormControl(0, [
                    Validators.required
                ]),
                usingThisAuthority: new FormControl(false, [
                    Validators.required
                ])
            }),
            edit: new FormGroup({
                currencyOperatingConditionIdList: new FormControl(
                    [],
                    [Validators.required]
                ),
                requiredOperatingConditionIdList: new FormControl([]),
                days: new FormControl(undefined),
                requiredFlights: new FormControl(0, [Validators.required]),
                requiredFlightTimeSeconds: new FormControl(0, [
                    Validators.required
                ]),
                usingThisAuthority: new FormControl(false, [
                    Validators.required
                ])
            })
        });

        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.refreshPilotList();
        this.refreshCurrency();

        this.pilotSearchSource
            .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
            .subscribe(search => {
                this.pilotSelectConfig = ngSelectPreSearchConfigure(
                    search,
                    this.pilotSelectConfig
                );
                this.refreshPilotList();
            });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.currencyConfigurationsSubject.complete();
        this.pilotSearchSource.complete();
    }

    private refreshPilotList() {
        this.loadingPilots = true;

        // No work tracker on this because we want a narrower working state
        this.personService
            .findPersonnelGraphQL(
                this.managingOrganisationId,
                this.pilotSelectConfig.currentPage,
                15,
                {
                    roleOrList: [PersonRolesDto.Roles.PILOT],
                    textSearch: this.pilotSelectConfig.searchValue || undefined
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    const newPilots = result.results.filter(
                        p =>
                            this.pilots.findIndex(
                                pilot => pilot.id === p.id
                            ) === -1
                    );
                    this.pilots = this.pilots.concat(newPilots);

                    this.pilotSelectConfig = updateNgSelectData(
                        result,
                        this.pilotSelectConfig
                    );

                    this.loadingPilots = false;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading pilots: ${error.message}`
                    );
                    this.loadingPilots = false;
                }
            });
    }

    onPilotSelectScroll() {
        const resultLength =
            this.pilotSelectConfig.searchValue == null
                ? this.pilots.length
                : this.pilots.filter(pilot =>
                      this.personSearch(
                          this.pilotSelectConfig.searchValue,
                          pilot
                      )
                  ).length;

        if (
            this.pilotSelectConfig.totalItems == null ||
            resultLength !== this.pilotSelectConfig.totalItems
        ) {
            this.refreshPilotList();
        }
    }

    onPilotSelectSearch(search: NgSelectSearch) {
        this.pilotSearchSource.next(search);
    }

    onPilotSelectClose() {
        this.pilotSelectConfig.searchValue = null;
    }

    refreshCurrency() {
        forkJoin([
            this.currencyService.findCurrencyConfiguration(
                this.personnelRegisterId,
                this.managingOrganisationId
            ),
            this.airspaceJurisdictionId != null
                ? this.jurisdictionService.findOperatingConditionCategories(
                      this.airspaceJurisdictionId
                  )
                : of([])
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([currencyConfig, operatingConditionCategories]) => {
                this.refreshCurrencyConfigurations(currencyConfig);
                this.operatingConditions = operatingConditionCategories.reduce(
                    (acc, v) =>
                        acc.concat(
                            v.values.map(val => ({
                                type:
                                    this.operatingConditionsTypeLookup[
                                        val.type
                                    ] ?? 'Misc',
                                value:
                                    val.type ===
                                    OperatingCategoryValuesDtoObject.Type
                                        .RPA_MTOW
                                        ? `<${this.formatMTOWPipe.transform(
                                              val.value as number
                                          )}`
                                        : this.operatingConditionsValueLookup[
                                              val.value
                                          ],
                                id: val.id
                            }))
                        ),
                    []
                );
            })
            .add(this.workTracker.createTracker());
    }

    refreshCurrencyConfigurations(currencyConfig: CurrencyConfigurationDto[]) {
        this.currencyConfigurationsSubject.next(currencyConfig);
        // Clear old data first
        this.currencyPreviewMap = {};

        this.refreshPreviewMap(currencyConfig)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: previewMap => (this.currencyPreviewMap = previewMap),
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading currency preview data: ${error.message}`
                    );
                    this.currencyPreviewMap = {} as Record<number, CurrencyResultDto>;
                }
            })
            .add(this.workTracker.createTracker());
    }

    refreshPreviewMap(
        currencyConfig: CurrencyConfigurationDto[]
    ): Observable<Record<number, CurrencyResultDto>> {
        if (!this.selectedPilotId) {
            return of({});
        }

        return this.currencyService
            .findCurrencyForPerson(
                this.personnelRegisterId,
                this.selectedPilotId,
                this.managingOrganisationId
            )
            .pipe(
                map(currencyResults =>
                    currencyConfig.reduce((acc, config) => {
                        const matchingResult = currencyResults.find(
                            result => result.id === config.id
                        );
                        if (matchingResult != null) {
                            acc[config.id] = matchingResult;
                        }
                        return acc;
                    }, {} as Record<number, CurrencyResultDto>)
                ),
                catchError(error => {
                    this.logging.error(
                        error,
                        `Error loading currency preview data: ${error.message}`
                    );
                    return of({} as Record<number, CurrencyResultDto>);
                })
            );
    }

    addConfig() {
        const value = this.formGroup.controls.add.value;
        const command: CreateCurrencyConfigurationCommand = {
            personnelRegisterId: this.personnelRegisterId,
            days: value.days,
            requiredFlights: value.requiredFlights,
            requiredFlightTimeSeconds: value.requiredFlightTimeSeconds,
            currencyOperatingConditionList:
                value.currencyOperatingConditionIdList,
            requiredOperatingConditionIdList:
                value.requiredOperatingConditionIdList,
            usingThisAuthority: value.usingThisAuthority
        };

        this.currencyService
            .createCurrencyConfiguration(command)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: value => {
                    this.logging.success(`Added configuration`);
                    this.formGroup.controls.add.reset();
                    this.refreshCurrency();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error adding configuration: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    updateConfig(config: CurrencyConfigurationDto) {
        const value = this.formGroup.controls.edit.value;
        const command: UpdateCurrencyConfigurationCommand = {
            days: value.days,
            requiredFlights: value.requiredFlights,
            requiredFlightTimeSeconds: value.requiredFlightTimeSeconds,
            currencyOperatingConditionIdList:
                value.currencyOperatingConditionIdList,
            requiredOperatingConditionIdList:
                value.requiredOperatingConditionIdList,
            usingThisAuthority: value.usingThisAuthority
        };
        this.currencyService
            .updateCurrencyConfiguration(config.id, command)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: value => {
                    this.logging.success(`Updated configuration`);
                    this.formGroup.controls.edit.reset();
                    this.editingConfig = null;
                    this.refreshCurrency();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error updating configuration: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    showEditMode(config: CurrencyConfigurationDto) {
        const currencyOperatingConditionList =
            config.currencyOperatingConditionList.reduce(
                (acc, c) => acc.concat(c.values.map(v => v.id)),
                []
            );
        const requiredOperatingConditionList =
            config.requiredOperatingConditionList.reduce(
                (acc, c) => acc.concat(c.values.map(v => v.id)),
                []
            );
        this.formGroup.controls.edit.setValue({
            currencyOperatingConditionIdList: currencyOperatingConditionList,
            requiredOperatingConditionIdList: requiredOperatingConditionList,
            days: config.days,
            requiredFlights: config.requiredFlights,
            requiredFlightTimeSeconds: config.requiredFlightTimeSeconds,
            usingThisAuthority: config.usingThisAuthority
        });
        this.formGroup.updateValueAndValidity();
        this.editingConfig = config.id;
    }

    deleteCurrencyConfig(config: CurrencyConfigurationDto) {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Delete currency configuration',
                'Are you sure you wish to delete this currency configuration?',
                'Delete',
                () =>
                    firstValueFrom(
                        this.currencyService.deleteCurrencyConfiguration(
                            config.id
                        )
                    )
            )
            .then(
                () => {
                    this.refreshCurrency();
                    doneWorking();
                },
                (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error deleting configuration: ${error.message}`
                    );
                    doneWorking();
                }
            )
            .catch(() => {
                doneWorking();
            });
    }

    cancelEditMode() {
        this.editingConfig = null;
        this.formGroup.controls.edit.reset();
    }

    typesGroup(item: OperatingCategoryValuesDtoOperatingCategoryDto) {
        return item.type;
    }
}
